import styled from 'styled-components';
import {
  ServicesSubSectionContainer,
  ServicesSubSectionHeader,
} from '../../services/services-subsection/services-subsection.styles';
import { FontSizes } from '../../../common/typography/typography.styles';
import { FieldContainer } from '../../../common/form-field-text/form-field-text.styles';
import { InputMessageContainer } from '../../../common/input-message/input-message.styles';
import { LinkButton } from '../../../common/button/button.styles';
import { BREAKPOINTS } from '../../../constants/breakpoints';

export const ParcelItemsManagementContainer = styled(
  ServicesSubSectionContainer
)`
  min-width: 0;

  ${LinkButton} {
    min-height: 3.2rem;
  }

  .container-add-new-item {
    display: flex;
    justify-content: end;

    button {
      width: fit-content;
    }
  }

  @media (max-width: ${BREAKPOINTS.mobile.css}) {
    ${ServicesSubSectionHeader} {
      flex-direction: column;
      align-items: start;
    }

    .container-add-new-item {
      button {
        width: 100%;
      }
    }
  }
`;

export const ParcelItemContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: space-between;
  min-width: 0;
`;

export const ParcelItemWrapper = styled.div`
  min-width: 0;
  width: 100%;
`;

export const ParcelInfo = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

export const ParcelProps = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 12rem) minmax(0, 1fr);
  align-items: center;
  row-gap: 8px;
  column-gap: 12px;
  min-width: 0;
`;

export const ActionIcons = styled.div`
  display: flex;
  align-items: start;
  flex-shrink: 0;
  gap: 1.6rem;
`;

export const ActionButton = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;
  display: flex;
`;

export const EditButton = styled(ActionButton)`
  color: ${({ theme }) => theme.colors.baseColors.blue125};

  &:disabled {
    color: #0070cca1;
  }
`;

export const RemoveButton = styled(ActionButton)`
  color: ${({ theme }) => theme.colors.baseColors.red130};

  &:disabled {
    color: #e82a5393;
  }
`;

export const ParcelItemHeaderContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 1.2rem;
  margin-bottom: 1.2rem;

  @media (max-width: ${BREAKPOINTS.mobile.css}) {
    flex-direction: column;
    gap: 0.4rem;
  }
`;

export const ParcelItemHeader = styled.span`
  font-size: ${FontSizes.medium};
  font-weight: 500;
  line-height: 2.2rem;
  letter-spacing: 0.5px;
  overflow-wrap: anywhere;
`;

export const FilledWithAiTag = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  padding: 0.4rem 1rem;
  border-radius: 2.4rem;
  background: linear-gradient(
    166.33deg,
    rgba(0, 198, 255, 0.1) 1.26%,
    rgba(0, 114, 255, 0.1) 117.54%
  );
  color: ${({ theme }) => theme.colors.baseColors.blue80};
  font-size: ${FontSizes.small};
  font-weight: 600;
  line-height: 1.6rem;
  letter-spacing: 0.5px;
  white-space: nowrap;

  .filled-with-ai-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 1.4rem;
    width: 1.4rem;
    height: 1.4rem;
    overflow: hidden;

    svg {
      width: 1.19017rem;
      height: 1.12rem;
    }
  }
`;

export const ParcelItemPropName = styled.span`
  color: ${({ theme }) => theme.colors.baseColors.grey100};
  font-size: ${FontSizes.small};
  font-style: normal;
  font-weight: 500;
  line-height: 1.6rem;
  letter-spacing: 0.5px;
`;

export const ParcelItemPropValue = styled.span`
  min-width: 0;
  font-size: ${FontSizes.normal};
  font-style: normal;
  font-weight: 500;
  line-height: 2rem;
  letter-spacing: 0.5px;
  overflow-wrap: anywhere;
`;

export const TextTotalValue = styled(ParcelItemPropValue)`
  font-size: ${FontSizes.medium};
`;

export const ParcelItemEditFormContainer = styled.div`
  min-width: 0;

  ${FieldContainer} {
    margin-bottom: 0;
  }
`;

export const FormInputs = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2.4rem;
  min-width: 0;
  margin-bottom: 2.4rem;

  > div,
  > div > * {
    min-width: 0;
  }

  @media (max-width: ${BREAKPOINTS.mobile.css}) {
    gap: 1.6rem;
  }

  > div:nth-child(1) {
    display: flex;
    gap: 2rem;

    & > div:nth-child(2) {
      max-width: 12rem;
    }

    @media (max-width: ${BREAKPOINTS.mobile.css}) {
      flex-direction: column;
      gap: 1.6rem;

      & > div:nth-child(2) {
        max-width: 100%;
      }
    }
  }

  > div:nth-child(2) {
    display: flex;
    gap: 2rem;

    @media (max-width: ${BREAKPOINTS.mobile.css}) {
      flex-direction: column;
      gap: 1.6rem;
    }
  }

  > ${InputMessageContainer} {
    margin-top: -0.6rem;
    grid-column: span 2;
  }
`;

export const ConditionSelector = styled.fieldset`
  display: flex;
  gap: 0.2rem;
  margin: 0;
  padding: 0.6rem;
  border: 0;
  border-radius: 1.2rem;
  background-color: ${({ theme }) => theme.colors.baseColors.grey5};
`;

export const ConditionOption = styled.label<{ $selected: boolean }>`
  position: relative;
  display: flex;
  flex: 1 1 0;
  align-items: center;
  justify-content: center;
  min-width: 0;
  padding: 0.6rem 1.6rem;
  border-radius: 1rem;
  background-color: ${({ $selected, theme }) =>
    $selected ? theme.colors.namedColors.white : 'transparent'};
  color: ${({ $selected, theme }) =>
    $selected
      ? theme.colors.namedColors.primary
      : theme.colors.baseColors.grey150};
  font-size: ${FontSizes.medium};
  font-weight: 500;
  line-height: 2.4rem;
  cursor: pointer;

  input {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  &:focus-within {
    outline: 0.2rem solid ${({ theme }) => theme.colors.namedColors.focus};
    outline-offset: 0.2rem;
  }
`;

interface BtnsBlockProps {
  $editMode: boolean;
}

export const FormBtnsBlock = styled.div<BtnsBlockProps>`
  display: flex;
  gap: 2.4rem;
  width: 34.4rem;
  margin-left: auto;

  width: ${({ $editMode }) => ($editMode ? '34.4rem' : '16rem')};

  @media (max-width: 650px) {
    width: 100%;
    flex-direction: column-reverse;
    gap: 1.6rem;
  }
`;

export const AiReviewControls = styled.div`
  .ai-review-progress {
    display: block;
    width: 100%;
    height: 0.4rem;
    border: 0;
    appearance: none;
    color: ${({ theme }) => theme.colors.namedColors.primary};
    background-color: ${({ theme }) => theme.colors.baseColors.grey25};

    &::-webkit-progress-bar {
      background-color: ${({ theme }) => theme.colors.baseColors.grey25};
    }

    &::-webkit-progress-value {
      background-color: ${({ theme }) => theme.colors.namedColors.primary};
      transition: width 0.2s linear;
    }

    &::-moz-progress-bar {
      background-color: ${({ theme }) => theme.colors.namedColors.primary};
    }
  }

  .ai-review-progress-label {
    margin-top: 0.8rem;
    margin-bottom: 2.4rem;
    color: ${({ theme }) => theme.colors.baseColors.grey100};
    font-size: ${FontSizes.normal};
    font-weight: 500;
    line-height: 2rem;
    letter-spacing: 0.5px;
    text-align: center;
  }

  .ai-review-buttons {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 2.4rem;
  }

  .ai-review-actions {
    display: flex;
    gap: 2.4rem;
    width: 34.4rem;
  }

  @media (max-width: ${BREAKPOINTS.mobile.css}) {
    .ai-review-progress-label {
      margin-bottom: 1.6rem;
    }

    .ai-review-buttons {
      flex-direction: column-reverse;
      gap: 1.6rem;
    }

    .ai-review-actions {
      gap: 1.6rem;
      width: 100%;
    }
  }
`;

export const Content = styled.div`
  display: grid;
  gap: 2.4rem;
  min-width: 0;

  > * {
    min-width: 0;
  }
`;

export const PopularCategoryMenuItem = styled.div`
  display: flex;
  gap: 1.2rem;
  align-items: center;
`;
